// What the Accelerator's own pages read and write, as the member themselves through row level
// security (docs/data-model.md 10.6.1). The product's shared rows (sessions, recordings, resources,
// the group's WhatsApp link, the welcome checklist) are the frame's (src/product/data.js); these
// are the mastermind's own: their standing (hub.my_mastermind), their goal, their months of
// actions, their wins, and the member directory. While staff view as a member the reads are that
// member's, and the pages call none of the writes (an update would change 0 rows and an insert be
// refused anyway).
import { supabase, must } from '../../lib/supabase.js'
import { callHubFunction } from '../../lib/functions.js'
import { GOAL_COLUMNS, CYCLE_COLUMNS, ACTION_COLUMNS, WIN_COLUMNS } from './columns.js'

const own = (table, columns, personId, productId) => supabase.from(table).select(columns)
  .eq('product_id', productId).eq('person_id', personId)

// Everything the home, Actions and Group pages draw from that the frame doesn't read, and their own accountability
// log (hub.my_accountability_log), read with the rest so the home's attendance, pill and refocus card are there on its
// first draw and nothing jumps in after (the Accelerator critique's members 1 and 11, 20 Oct). The log is the
// heaviest read and the home works without it: if it fails, log is null (with logFailed, for the log page's own line)
// and the home shows no attendance line, no pill and no refocus card, never an error.
export async function loadMastermind(productId, personId) {
  let logFailed = false
  const [standing, goals, cycles, actions, wins, log] = await Promise.all([
    supabase.rpc('my_mastermind', { p_product: productId }).then(must),
    own('goals', GOAL_COLUMNS, personId, productId).order('id').then(must),
    own('cycles', CYCLE_COLUMNS, personId, productId).order('month', { ascending: false }).order('id').then(must),
    own('actions', ACTION_COLUMNS, personId, productId).order('sort_order').order('id').then(must),
    own('wins', WIN_COLUMNS, personId, productId).order('win_date', { ascending: false }).order('created_at', { ascending: false }).order('id').then(must),
    loadMyLog(productId).catch(() => { logFailed = true; return null }),
  ])
  return { standing: standing || [], goal: (goals || [])[0] || null, cycles: cycles || [], actions: actions || [], wins: wins || [], log: log || null, logFailed }
}

// Their own accountability log (hub.my_accountability_log): it gives the reader's own record and takes no person, so
// nobody can ask for anyone else's; while staff view as a member it's that member's
export async function loadMyLog(productId) {
  return (await supabase.rpc('my_accountability_log', { p_product: productId }).then(must)) ?? null
}

// The opted-in members of the whole Accelerator, every group (hub.group_directory; Adam's answer 1 of
// 7 Oct): name, their group, where they invest, about them and whether they have a photo, never an
// email or phone
export async function loadDirectory(productId) {
  return (await supabase.rpc('group_directory', { p_product: productId }).then(must)) || []
}

// 0 rows back means the write changed nothing (not open to them now): a refusal, not a save
const one = rows => {
  if (!rows || !rows.length) throw Object.assign(new Error('nothing saved'), { code: 'not_saved' })
  return rows[0]
}

// A tick (the database stamps it now) or an untick
export async function tickAction(actionId, done) {
  return one(await supabase.from('actions').update({ done_at: done ? new Date().toISOString() : null })
    .eq('id', actionId).select(ACTION_COLUMNS).then(must))
}

// Their note on an action; an empty note is none
export async function saveNote(actionId, note) {
  return one(await supabase.from('actions').update({ member_note: note || null })
    .eq('id', actionId).select(ACTION_COLUMNS).then(must))
}

// Their goal: a new draft, or the draft changed
export async function saveGoal({ goalId, personId, productId, body }) {
  if (goalId) return one(await supabase.from('goals').update({ body }).eq('id', goalId).select(GOAL_COLUMNS).then(must))
  return one(await supabase.from('goals').insert({ person_id: personId, product_id: productId, body }).select(GOAL_COLUMNS).then(must))
}

// A win: today's unless a date is given, with its more about it (none when blank) and whether Adam may
// share it
export async function addWin({ personId, productId, title, category, detail, shareable }) {
  return one(await supabase.from('wins').insert({ person_id: personId, product_id: productId, title, category, detail: detail || null, shareable })
    .select(WIN_COLUMNS).then(must))
}

// Their own win changed: its words, its kind, its more about it (blank clears it; Adam's yes of 11 Oct) and
// whether Adam may share it (Adam's answer 6 of 7 Oct), only on a win they logged. The database trims the
// words and the more about it and checks them and the kind (23514); a win that isn't theirs, or while their
// access is paused, changes 0 rows
export async function saveWin({ winId, title, category, detail, shareable }) {
  return one(await supabase.from('wins').update({ title, category, detail: detail || null, shareable }).eq('id', winId)
    .select(WIN_COLUMNS).then(must))
}

// The share tick alone, on a win staff logged for them: the 1 thing on it that's theirs to change
// (Adam, 7 Oct; the database refuses any other change from them with 42501 win_logged_by_staff)
export async function saveShare(winId, shareable) {
  return one(await supabase.from('wins').update({ shareable }).eq('id', winId)
    .select(WIN_COLUMNS).then(must))
}

// Their own win deleted; 0 rows back means nothing was (a win staff logged can't be deleted by them)
export async function deleteWin(winId) {
  return one(await supabase.from('wins').delete().eq('id', winId).select('id').then(must))
}

// Profile photos for the directory: links signed for 2 minutes, for up to 40 people a call
// (sign-file's avatar branch); anyone left out has no photo to show, and their initials are drawn
export async function signAvatars(personIds) {
  const out = {}
  for (let i = 0; i < personIds.length; i += 40) {
    const batch = personIds.slice(i, i + 40)
    try {
      const d = await callHubFunction('sign-file', { kind: 'avatar', person_ids: batch })
      for (const [id, a] of Object.entries(d?.avatars || {})) {
        if (typeof a?.url === 'string' && a.url.startsWith('https://')) out[id] = a.url
      }
    } catch { /* initials for this batch */ }
  }
  return out
}
